import { createContext, useContext, type ReactNode, useState } from 'react';
import { type OnChangeTypes } from '../Builder/RespostaEditor';
import { type ShadowItem } from '../../../ui/ShadowPallete/ShadowPallete';
import defaultValueFaq from '../defaultValueFaq';
import { makeID } from '../../../lib/utils';

export interface FAQPergunta {
	id: string;
	pergunta: string;
	resposta: OnChangeTypes;
}

export interface FAQIcon {
	name?: string;
	key?: string;
	label?: string;
	icon?: React.ReactNode;
}

export interface FAQCategoria {
	id: string;
	titulo: OnChangeTypes;
	subtitulo: OnChangeTypes;
	perguntas: FAQPergunta[];
	styles?: FAQStyles;
	icon?: FAQIcon;
	layout: string;
}

export interface FAQStyles {
	backgroundColorFaq?: string;
	perguntaBackgroundColor?: string;
	respostaBackgroundColor?: string;
	corTitulo?: string;
	corSubtitulo?: string;
	corPergunta?: string;
	corResposta?: string;
	perguntasGap?: number;
	borderRadiusFaq?: number;
	borderRadiusPergunta?: number;
	shadowFaq?: ShadowItem;
	borderWidthFaq?: number;
	borderColorFaq?: string;
	borderColorResposta?: string;
	borderWidthResposta?: number;
	borderStyleFaq?: 'solid' | 'dashed' | 'dotted';
	borderStyleResposta?: 'solid' | 'dashed' | 'dotted';
	corIcon?: string;
}

export const faqPerguntaDefault = {
	id: '',
	pergunta: '',
	resposta: {
		html: "<p></p>",
		json: {
			type: "doc",
			content: []
		},
		plain: ""
	},
	styles: {},
	icon: {},
	layout: ''
}

export const faqDefault = {
	titulo: {
			html: 'Perguntas Frequentes',
			json: {
				"type": "doc",
				"content": []
			},
			plain: 'Perguntas Frequentes'
		},
		subtitulo: {
			html: 'Tudo o que você precisa saber sobre nossos produtos e serviços.',
			json: {
				"type": "doc",
				"content": []
			},
			plain: 'Tudo o que você precisa saber sobre nossos produtos e serviços.'
		},
		styles: {

		},
		icon: {},
		layout: 'accordion',
		perguntas: defaultValueFaq
}

interface FAQEditorContextType {
	faq: FAQCategoria;
	setFAQ: (newFAQ: FAQCategoria) => void;
	addPergunta: (pergunta: FAQPergunta) => void;
	changeFaq: (id: string, changes: Partial<FAQPergunta>) => void;
	excluirFaq: (id: string) => void;
	setList: (newOrder: FAQPergunta[]) => void;
	changeFaqStyles: (styles: FAQStyles) => void;
	changeFaqIcon: (icon: FAQIcon) => void;
	changeFaqLayout: (layout: string) => void;
	mode: 'preview' | 'edit',
	endPoint: string,
	changeMode: (mode: 'preview' | 'edit') => void,
	changeSimpleMode: (simple: boolean) => void,
	simpleMode: boolean,
	changeTitulo: (titulo: OnChangeTypes) => void;
	changeSubTitulo: (subtitulo: OnChangeTypes) => void;
	onSave: () => void;
	onFaqSave: (fn: () => void) => void;
	setEndPoint: (endpoint: string) => void;
}

const FAQEditorContext = createContext<FAQEditorContextType | undefined>(undefined);

export function FAQEditorProvider({
	children,
}: { children: ReactNode }) {
	const [onSaveFn, setOnSaveFn] = useState<() => void>(() => () => { });

	const [mode, setMode] = useState<'preview' | 'edit'>('edit');
	const [endPoint, setEndPointState] = useState<string>('');
	const [simpleMode, setSimpleMode] = useState<boolean>(false);

	const [faq, setFAQ] = useState<FAQCategoria>({
		id: makeID(),
		...faqDefault,
	});

	function addPergunta(pergunta: FAQPergunta) {
		setFAQ((prev) => ({
			...prev,
			perguntas: [...prev.perguntas, pergunta],
		}));
	}

	const changeTitulo = (titulo: OnChangeTypes) => {
		setFAQ({
			...faq,
			titulo: titulo,
		});
	};

	const onFaqSave = (fn: (faq: FAQCategoria) => void) => {
		setOnSaveFn(() => fn);
	};

	const onSave = () => {
		onSaveFn();
	};

	const changeSubTitulo = (subtitulo: OnChangeTypes) => {
		setFAQ({
			...faq,
			subtitulo: subtitulo,
		});
	}

	const changeFaq = (id: string, changes: Partial<FAQPergunta>) => {
		setFAQ({
			...faq,
			perguntas: faq.perguntas.map((item) =>
				item.id === id ? { ...item, ...changes } : item
			),
		});
	};

	const excluirFaq = (id: string) => {
		setFAQ({
			...faq,
			perguntas: faq.perguntas.filter((item) => item.id !== id),
		});
	};

	const setList = (newOrder: FAQPergunta[]) => {
		setFAQ({
			...faq,
			perguntas: newOrder
		})
	};

	const changeFaqStyles = (styles: FAQStyles) => {
		setFAQ({
			...faq,
			styles: {
				...faq.styles,
				...styles,
			},
		});
	}

	const changeFaqIcon = (icon: FAQIcon) => {
		setFAQ({
			...faq,
			icon: {
				...faq.icon,
				...icon,
			},
		});
	}

	const changeFaqLayout = (layout: string) => {
		setFAQ({
			...faq,
			layout: layout,
		});
	}

	const changeMode = (mode: 'preview' | 'edit') => {
		setMode(mode);
	};

	const changeSimpleMode = (simple: boolean) => {
		setSimpleMode(simple);
	}

	const setEndPoint = (endPoint: string) => {
		setEndPointState(endPoint);
	};

	return <FAQEditorContext.Provider value={{
		endPoint, setEndPoint, faq, mode, changeTitulo, changeSubTitulo, changeMode, setFAQ, addPergunta, changeFaq, excluirFaq, setList, changeFaqStyles, changeFaqIcon, changeFaqLayout, changeSimpleMode, simpleMode, onSave,
		onFaqSave
	}}>{children}</FAQEditorContext.Provider>;
}

export function useFAQEditor() {
	const context = useContext(FAQEditorContext);
	if (!context) throw new Error('useFAQEditor deve ser usado dentro do FAQEditorProvider');
	return context;
}
